import React, { useState } from "react";
import { adminApi, formatApiError, resolveImageUrl } from "../lib/adminApi";
import { Upload, Loader2, X } from "lucide-react";

/**
 * ImageUpload: lets admin upload a file (returns URL stored in `value`)
 * or paste any image URL manually.
 */
export default function ImageUpload({ value, onChange, label = "Imagen", testId = "image-upload", removeBg = false, onMeta = null }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setErr("");
    setInfo("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = removeBg ? { remove_bg: "true" } : {};
      const { data } = await adminApi.post("/upload", fd, { params });
      onChange(data.url);
      if (onMeta) onMeta(data);
      if (data.original_size && data.size) {
        const orig = (data.original_size / 1024).toFixed(0);
        const optim = (data.size / 1024).toFixed(0);
        const pct = Math.round((1 - data.size / data.original_size) * 100);
        const transp = data.has_transparency ? " · con transparencia (silueta)" : " · sin transparencia (a color)";
        setInfo(`Optimizada: ${orig} KB → ${optim} KB (${pct}% menos)${removeBg ? transp : ""}`);
      }
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <label className="admin-label">{label}</label>
      <div className="flex gap-3 items-stretch">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://... o sube un archivo"
          className="admin-input flex-1"
          data-testid={`${testId}-url`}
        />
        <label className="admin-btn-ghost cursor-pointer flex items-center gap-2 px-4">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          <span>{busy ? "Subiendo..." : "Subir"}</span>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFile}
            disabled={busy}
            data-testid={`${testId}-file`}
          />
        </label>
      </div>
      {err && <p className="text-xs text-red-400">{err}</p>}
      {info && <p className="text-xs text-[var(--pc-bronze)]" data-testid={`${testId}-info`}>{info}</p>}
      {value && (
        <div className="relative inline-block mt-2 border border-[var(--pc-line)]">
          <img
            src={resolveImageUrl(value)}
            alt="preview"
            className="h-28 w-auto object-cover"
            onError={(e) => {
              e.target.style.opacity = "0.3";
            }}
          />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 w-6 h-6 bg-black border border-[var(--pc-line)] text-[var(--pc-text)] flex items-center justify-center hover:text-[var(--pc-bronze-bright)]"
            data-testid={`${testId}-clear`}
            aria-label="Quitar imagen"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
